import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { languageContext } from '@/context/language.context';
import Form from 'react-bootstrap/Form';
import Button from '@/components/Button/Button';
import classes from './LoginPage.module.scss';
import t from '@/utils/Language/Modal/LoginModal-translations';
import useAccount from '@/hooks/useAccount';
import useSaml from '@/hooks/useSaml';
import { regexFilledField } from '@/utils/utils';
import ReCAPTCHA from 'react-google-recaptcha';
import { modalContext } from '@/context/modal.context';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import Disclaimer from '@/components/Disclaimer/Disclaimer';
import { SolutionContext } from '@/context/solution.context';
import useRecaptcha from '@/hooks/useRecaptcha';

const LoginForm = ({ context, onLoginComplete, signupModalProps, startingEmailOrPhoneInputValue, ...props }) => {
  const { currentLanguage } = useContext(languageContext);
  const {
    register,
    formState: { errors }
  } = useForm();

  const [loginError, setLoginError] = useState(null);
  const { acctLogin, parseForm } = useAccount();
  const { samlPartner, handleBlurOnEmailOrPhone, handleKeyPressOnEmailOrPhone, handleSAMLRedirect, samlConfig } = useSaml();
  const { enqueueModal } = useContext(modalContext);
  const [loginAttemptCount, setLoginAttemptCount] = useState(0);
  const [processing, setProcessing] = useState(false);
  const maxLoginAttempts = 3;
  const { solution } = useContext(SolutionContext);
  const { requireCaptcha, captchaState, onChangeCaptcha, incrementFailedAttempts } = useRecaptcha();
  const modal = useContext(modalContext);
  const router = useRouter();
  const [mfa, setMFA] = useState({ required: false });
  const [rememberCreds, setRememberCreds] = useState({});

  // TODO: The disabling of the submit button might have gotten broken by the solution code,
  // but we will want something back. (Broken in production before MFA was merged in.)
  // There is now a preventSubmit in this version, but doesn't grey out to start.
  // This can be worked on later.
  //const disableSubmit = (requireCaptcha && !captchaState) || samlConfig === false || processing;

  const trackLoginAttempt = async (e) => {
    e.preventDefault();
    let requiresMFA = false; // whether the response is prompting for MFA token next
    setProcessing(true);
    if (handleSAMLRedirect()) {
      return;
    }
    try {
      const req = parseForm(e);
      const params = { login_key: req.email || rememberCreds.login_key, password: req.password || rememberCreds.password };
      const otp = mfa.required ? { token: req.otp_token, hash: mfa.hash } : null;
      const data = await acctLogin(params, otp);
      if (data?.token_hash) {
        // if the response is an object with a token_hash, then we need to prompt for a one-time password for MFA
        setMFA({ required: true, hash: data.token_hash, method: data.mfa_method });
        requiresMFA = true;
        setRememberCreds(params); // will need to re-send these when we send the OTP
      } else {
        onLoginComplete(data);
      }
    } catch (err) {
      console.warn(err);
      setLoginError(err);
    } finally {
      if (!requiresMFA) setLoginAttemptCount(loginAttemptCount + 1); // TODO skip on complete but needs mfa token?
      setProcessing(false);
    }
  };

  const openForgotPasswordModal = () => {
    modal.enqueueModal(
      {
        name: 'ForgotPasswordModal',
        reason: 'forgot_password'
      },
      { forceOpen: true }
    );
  };

  const openSignup = (e) => {
    e.preventDefault();
    if (context == 'modal') {
      enqueueModal(
        {
          name: 'SignupModal',
          startingEmailOrPhoneInputValue,
          ...signupModalProps,
          backdrop: typeof props.backdrop !== 'undefined' ? props.backdrop : true,
          preventClose: props.preventClose
        },
        { forceOpen: true }
      );
    } else {
      router.push('/signup');
    }
  };

  const isSAML = samlPartner !== false;
  const showMFA = mfa.required;
  const showSignUpLink = !isSAML && !showMFA && solution.includes?.sign_up;
  const showEmailInput = !isSAML && !showMFA;
  const showPasswordInput = !isSAML && !showMFA;
  const showForgotPasswordLink = !isSAML;

  const preventSubmit = (loginAttemptCount >= maxLoginAttempts && !captchaState.value) || samlConfig === false || processing;

  return (
    <>
      {showSignUpLink && (
        <>
          <p className={classes.bodyTitle}>
            {t("Don't have an account?")}{' '}
            <a href="#" onClick={openSignup}>
              {t('Sign up.')}
            </a>
          </p>
          <p className={classes.divider}>
            <span className={[`${classes.dividerLine} ${classes.dividerLineL}`]}></span>
            <span className={classes.dividerText}>{t('or')}</span>
            <span className={[`${classes.dividerLine} ${classes.dividerLineR}`]}></span>
          </p>
        </>
      )}
      {isSAML && <p dangerouslySetInnerHTML={{ __html: t(samlPartner.login_prompt_key) }} />}
      <Form onSubmit={trackLoginAttempt}>
        {showEmailInput && (
          <Form.Group className="mb-3" controlId="formBasicEmail">
            <Form.Control
              data-cy="modal-login-input-email"
              type="text"
              placeholder={t('Email or cell phone')}
              defaultValue={startingEmailOrPhoneInputValue}
              onKeyPress={handleKeyPressOnEmailOrPhone}
              {...register('email', { onBlur: handleBlurOnEmailOrPhone, required: true })}
              pattern={regexFilledField}
            />
            <label>{t('Email or cell phone')}</label>
          </Form.Group>
        )}
        {showPasswordInput && (
          <Form.Group className="mb-0" controlId="formBasicPassword">
            <Form.Control
              data-cy="modal-login-input-password"
              className={`${classes.loginField} ${classes.middleField}`}
              type="password"
              placeholder={t('Password')}
              {...register('password', { required: true })}
              pattern={regexFilledField}
            />
            <label>{t('Password')}</label>
          </Form.Group>
        )}
        {showMFA && (
          <div>
            <p className={classes.bodyTitle}>{t('otp_sent')}</p>
            <Form.Group className="mb-3" controlId="formBasicToken">
              <Form.Control
                data-cy="modal-login-input-otp"
                type="text"
                placeholder={t('One-time code')}
                {...register('otp_token', { required: true })}
                pattern={regexFilledField}
              />
              <label>{t('One-time code')}</label>
            </Form.Group>
            <p>
              {' '}
              <a href="/login" className={classes.link}>
                {t('reset_mfa')}
              </a>
            </p>
          </div>
        )}
        <p>
          {errors.email && <span>{t('Email is required')}</span>}
          {errors.password && <span>{t('Password is required')}</span>}
          {loginError && <span>{loginError}</span>}
        </p>
        {!showMFA && showForgotPasswordLink && (
          <p className={classes.forgotPassword}>
            <a className={classes.link} onClick={openForgotPasswordModal}>
              {t('Forgot password?')}
            </a>
          </p>
        )}
        {requireCaptcha && !processing && (
          <div>
            <p>{t('Too many failed login attempts. Please confirm CAPTCHA and then try again.')}</p>
            <ReCAPTCHA className={classes.captcha} hl={currentLanguage} sitekey={process.env.RECAPTCHA_PUBLIC_KEY} onChange={onChangeCaptcha} />
          </div>
        )}
        <Button
          type="primary"
          disabled={preventSubmit}
          dataCy={'modal-login-btn-login'}
          styles={{ width: 'full' }}
          text={isSAML ? t(samlPartner.login_button_key) : t('Log in')}
          htmlType="submit"
        />
      </Form>
      {!isSAML && <Disclaimer />}
    </>
  );
};

LoginForm.propTypes = {
  context: PropTypes.string,
  onLoginComplete: PropTypes.func,
  signupModalProps: PropTypes.object,
  startingEmailOrPhoneInputValue: PropTypes.string
};

LoginForm.defaultProps = {
  onLoginComplete: () => {},
  signupModalProps: {}
};

export default LoginForm;
